import React from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@material-ui/core";
import { Description } from "@material-ui/icons";
import HelpLayout from "../../components/HelpCenter/HelpLayout";
import { useHelpCategories, useHelpArticles } from "../../hooks/useHelp";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(8),
  },
  header: {
    marginBottom: theme.spacing(4),
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  description: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  articleCard: {
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[3],
    },
  },
  articleList: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
  },
  articleItem: {
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  subcategoryCard: {
    height: "100%",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[3],
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
}));

const HelpCategoryPage = () => {
  const classes = useStyles();
  const { slug } = useParams();
  const history = useHistory();

  const { categories, loading: categoriesLoading } = useHelpCategories({
    searchParam: "",
    parentId: null,
  });

  // Encontrar a categoria atual primeiro
  const currentCategory = categories.find((cat) => cat.slug === slug);

  const { articles, loading: articlesLoading } = useHelpArticles({
    categoryId: currentCategory?.id,
  });

  const loading = categoriesLoading || articlesLoading;

  const getCurrentLang = () => {
    const lang = i18n.language || "pt";
    return lang.toLowerCase().substring(0, 2);
  };

  const getCategoryName = (category) => {
    const lang = getCurrentLang();
    if (lang === "en") return category.nameEn;
    if (lang === "es") return category.nameEs;
    return category.namePt;
  };

  const getCategoryDescription = (category) => {
    const lang = getCurrentLang();
    if (lang === "en") return category.descriptionEn;
    if (lang === "es") return category.descriptionEs;
    return category.descriptionPt;
  };

  const getArticleTitle = (article) => {
    const lang = getCurrentLang();
    if (lang === "en") return article.titleEn;
    if (lang === "es") return article.titleEs;
    return article.titlePt;
  };

  const getArticleDescription = (article) => {
    const lang = getCurrentLang();
    if (lang === "en") return article.descriptionEn;
    if (lang === "es") return article.descriptionEs;
    return article.descriptionPt;
  };

  const handleArticleClick = (article) => {
    history.push(`/help/article/${article.slug}`);
  };

  const handleSubcategoryClick = (subcategory) => {
    history.push(`/help/${subcategory.slug}`);
  };

  if (loading) {
    return (
      <HelpLayout>
        <Box className={classes.loading}>
          <CircularProgress />
        </Box>
      </HelpLayout>
    );
  }

  if (!currentCategory) {
    return (
      <HelpLayout>
        <Box className={classes.emptyState}>
          <Typography variant="h5">
            {i18n.t("helpCenter.categoryNotFound")}
          </Typography>
        </Box>
      </HelpLayout>
    );
  }

  const breadcrumbs = [
    {
      label: getCategoryName(currentCategory),
    },
  ];

  const hasSubcategories =
    currentCategory.subcategories && currentCategory.subcategories.length > 0;

  return (
    <HelpLayout breadcrumbs={breadcrumbs}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {getCategoryName(currentCategory)}
        </Typography>
        {getCategoryDescription(currentCategory) && (
          <Typography variant="h6" className={classes.description}>
            {getCategoryDescription(currentCategory)}
          </Typography>
        )}
      </Box>

      {/* Subcategorias */}
      {hasSubcategories && (
        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>
            {i18n.t("helpCenter.subcategories")}
          </Typography>
          <Grid container spacing={3}>
            {currentCategory.subcategories.map((subcategory) => (
              <Grid item xs={12} sm={6} md={4} key={subcategory.id}>
                <Card className={classes.subcategoryCard}>
                  <CardActionArea
                    onClick={() => handleSubcategoryClick(subcategory)}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {getCategoryName(subcategory)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {getCategoryDescription(subcategory)}
                      </Typography>
                      {subcategory.articles && subcategory.articles.length > 0 && (
                        <Typography
                          variant="caption"
                          color="primary"
                          style={{ marginTop: 8, display: "block" }}
                        >
                          {subcategory.articles.length}{" "}
                          {subcategory.articles.length === 1
                            ? i18n.t("helpCenter.article")
                            : i18n.t("helpCenter.articles")}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Artigos */}
      {articles && articles.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="h5" className={classes.sectionTitle}>
            {i18n.t("helpCenter.articles")}
          </Typography>
          <List className={classes.articleList}>
            {articles.map((article, index) => (
              <React.Fragment key={article.id}>
                {index > 0 && <Divider />}
                <ListItem
                  button
                  className={classes.articleItem}
                  onClick={() => handleArticleClick(article)}
                >
                  <Description
                    color="primary"
                    style={{ marginRight: 16 }}
                  />
                  <ListItemText
                    primary={getArticleTitle(article)}
                    secondary={getArticleDescription(article)}
                    primaryTypographyProps={{
                      variant: "subtitle1",
                      style: { fontWeight: 500 },
                    }}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}

      {/* Estado vazio */}
      {!hasSubcategories && (!articles || articles.length === 0) && (
        <Box className={classes.emptyState}>
          <Typography variant="h6">
            {i18n.t("helpCenter.noContentYet")}
          </Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            {i18n.t("helpCenter.contentComingSoon")}
          </Typography>
        </Box>
      )}
    </HelpLayout>
  );
};

export default HelpCategoryPage;
